import { inputAnatomy as parts } from '@chakra-ui/anatomy'
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleObject,
} from '@chakra-ui/theme-tools'
import { getColor, mode } from '@chakra-ui/theme-tools'

const baseStyle: PartsStyleObject<typeof parts> = {
  field: {
    width: '100%',
    minWidth: 0,
    outline: 0,
    position: 'relative',
    appearance: 'none',
    transitionProperty: 'common',
    transitionDuration: 'normal',
    fontFamily: 'Poppins',
    fontWeight: 'medium',
    boxShadow: 'none',
    color: 'appSlate',
    _disabled: {
      background: 'appGrey',
    },
    _placeholder: {
      color: 'appSlate',
      opacity: 0.5,
    },
  },
}

const size: Record<string, SystemStyleObject> = {
  lg: {
    fontSize: 'lg',
    px: 5,
    h: 12,
    borderRadius: 'base',
  },

  md: {
    fontSize: 'md',
    px: 5,
    h: 10,
    borderRadius: 'base',
  },

  sm: {
    fontSize: 'sm',
    px: 4,
    h: 8,
    borderRadius: 'sm',
  },

  xs: {
    fontSize: 'xs',
    px: 2,
    h: 6,
    borderRadius: 'sm',
  },
}

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
  lg: {
    field: size.lg,
    addon: size.lg,
  },
  md: {
    field: size.md,
    addon: size.md,
  },
  sm: {
    field: size.sm,
    addon: size.sm,
  },
  xs: {
    field: size.xs,
    addon: size.xs,
  },
}

function getDefaults(props: Record<string, any>) {
  const { focusBorderColor: fc, errorBorderColor: ec } = props
  return {
    focusBorderColor: fc || mode('blue.500', 'blue.300')(props),
    errorBorderColor: ec || mode('red.500', 'red.300')(props),
  }
}

const variantOutline: PartsStyleFunction<typeof parts> = props => {
  const { theme } = props
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props)

  return {
    field: {
      border: '1px solid',
      borderColor: 'appPaleLilac',
      bg: 'inherit',
      _hover: {
        boxShadow: 'none',
        borderColor: 'appPaleLilac',
      },
      _readOnly: {
        userSelect: 'all',
        boxShadow: 'none',
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
        boxShadow: 'none',
      },
      _invalid: {
        borderColor: getColor(theme, ec),
        boxShadow: 'none',
      },
      _focus: {
        zIndex: 1,
        borderColor: getColor(theme, fc),
        boxShadow: 'none',
      },
    },
    addon: {
      border: '1px solid',
      borderColor: 'appPaleLilac',
      bg: 'transparent',
    },
  }
}

const variantFilled: PartsStyleFunction<typeof parts> = props => {
  const { theme } = props
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props)

  return {
    field: {
      border: '2px solid',
      borderColor: 'transparent',
      bg: mode('gray.100', 'whiteAlpha.50')(props),
      _hover: {
        bg: mode('gray.200', 'whiteAlpha.100')(props),
        boxShadow: 'none',
      },
      _readOnly: {
        userSelect: 'all',
        boxShadow: 'none',
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
        boxShadow: 'none',
      },
      _invalid: {
        borderColor: getColor(theme, ec),
        boxShadow: 'none',
      },
      _focus: {
        bg: 'transparent',
        borderColor: getColor(theme, fc),
        boxShadow: 'none',
      },
    },
    addon: {
      border: '2px solid',
      borderColor: 'transparent',
      bg: 'transparent',
    },
  }
}

const variantFlushed: PartsStyleFunction<typeof parts> = props => {
  const { theme } = props
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props)

  return {
    field: {
      borderBottom: '1px solid',
      borderColor: 'inherit',
      borderRadius: 0,
      px: 0,
      bg: 'transparent',
      _readOnly: {
        userSelect: 'all',
        boxShadow: 'none',
      },
      _invalid: {
        borderColor: getColor(theme, ec),
        boxShadow: 'none',
      },
      _focus: {
        borderColor: getColor(theme, fc),
        boxShadow: 'none',
      },
    },
    addon: {
      borderBottom: '2px solid',
      borderColor: 'inherit',
      borderRadius: 0,
      px: 0,
      bg: 'transparent',
    },
  }
}

const variantUnstyled: PartsStyleObject<typeof parts> = {
  field: {
    bg: 'transparent',
    px: 0,
    height: 'auto',
  },
  addon: {
    bg: 'transparent',
    px: 0,
    height: 'auto',
  },
}

const variants = {
  outline: variantOutline,
  filled: variantFilled,
  flushed: variantFlushed,
  unstyled: variantUnstyled,
}

const defaultProps = {
  size: 'md',
  variant: 'outline',
  focusBorderColor: 'appClearBlue',
  errorBorderColor: 'appBrick',
}

export default {
  parts: parts.keys,
  baseStyle,
  sizes,
  variants,
  defaultProps,
}
